import type { CaptureFileJSONObject } from '@ikegps/core-api';
import { IkeId } from '../../../id';
import { CollectionModel } from '../collection.data';
import type {
	CollectionModelCropParameter,
	ModelStateCropParameter,
	ModelStateCropParameterTag,
} from './cropParameter';
import { CollectionModelFreeCropParameter } from './free.cropParameter';

export type CropParameterId = string;
export type ModelFieldStateCropParameter = CropParameterId[];

function makeCropParameterState(tag: ModelStateCropParameterTag, id: string): ModelStateCropParameter {
	switch (tag) {
		case 'free':
			return CollectionModelFreeCropParameter.state(id);
		default:
			throw new Error(`Unknown marker type: "${tag}"`);
	}
}

export class CollectionModelCropParameterStateMutator {
	state: CollectionModelCropParameterState;
	constructor(state: CollectionModelCropParameterState) {
		this.state = state;
	}

	getState(): ModelFieldStateCropParameter {
		const collectionState = this.state.collection.state;

		const captureCropParameterKey = CollectionModelCropParameterState.captureCropParameterId(this.state.captureId);
		const state = collectionState.getFieldValue(captureCropParameterKey) as ModelFieldStateCropParameter;
		if (state == null) {
			return [];
		}

		return state.slice();
	}

	setState(state: ModelFieldStateCropParameter) {
		const collectionState = this.state.collection.state;
		const captureCropParameterKey = CollectionModelCropParameterState.captureCropParameterId(this.state.captureId);
		collectionState.setFieldValue(captureCropParameterKey, state);
	}

	create(tag: ModelStateCropParameterTag, id = IkeId.generate()): CropParameterId {
		const state = makeCropParameterState(tag, id);
		return this.createWithState(state);
	}

	remove(cropParameter: CollectionModelCropParameter) {
		const cropParameterIds = this.getState();
		const index = cropParameterIds.indexOf(cropParameter.id);
		if (index === -1) {
			throw new Error(
				`Cannot remove crop correction. Instance "${cropParameter.id}" is missing from capture "${this.state.captureId}"`
			);
		}

		cropParameterIds.splice(index, 1);
		this.setState(cropParameterIds);

		delete this.state.cropParameters[cropParameter.id];
		cropParameter.Mutator.destroyState();
	}

	clear() {
		const cropParameters = this.state.values();
		for (const cropParameter of cropParameters) {
			const cropParameterIds = this.getState();
			const index = cropParameterIds.indexOf(cropParameter.id);
			if (index === -1) {
				continue;
			}
			this.remove(cropParameter);
		}
	}

	private createWithState(state: ModelStateCropParameter): CropParameterId {
		const collectionState = this.state.collection.state;
		const captureId = this.state.captureId;

		const stateId = CollectionModelCropParameterState.cropParameterId(captureId, state.id);
		if (collectionState.fields.get(stateId) != null) {
			throw new Error(`Crop Correction "${stateId}" already exists`);
		}

		collectionState.setFieldValue(stateId, state);

		const cropParameterState: ModelFieldStateCropParameter = this.getState();
		cropParameterState.push(state.id);
		this.setState(cropParameterState);

		return state.id;
	}
}

const ID_PREFIX_CROP_CORRECTION = 'c';
const ID_PREFIX_CAPTURE_CROP_CORRECTION = 'cl';

export class CollectionModelCropParameterState {
	static cropParameterId(captureId: string, cropParameterId: string): string {
		return `${ID_PREFIX_CROP_CORRECTION}:${captureId}:${cropParameterId}`;
	}

	static captureCropParameterId(captureId: string): string {
		return `${ID_PREFIX_CAPTURE_CROP_CORRECTION}:${captureId}`;
	}

	static getCaptureCropParameterId(key: string): string | null {
		if (key.startsWith(`${ID_PREFIX_CAPTURE_CROP_CORRECTION}:`)) {
			return key.substr(3);
		}

		return null;
	}
	Mutator: CollectionModelCropParameterStateMutator = new CollectionModelCropParameterStateMutator(this);
	id = IkeId.generate();
	collection: CollectionModel;
	captureId: string;

	cropParameters: { [key: string]: CollectionModelCropParameter } = {};

	constructor(collection: CollectionModel, captureId: string) {
		this.collection = collection;
		this.captureId = captureId;
	}

	useInExportCorrection(cropParameterId: string) {
		if (!this.hasCropParameter(cropParameterId)) {
			return false;
		} else {
			let brightness = this.getCropParameter(cropParameterId);
			switch (brightness.state.tag) {
				case 'free':
					const free = this.getCropParameter<CollectionModelFreeCropParameter>(cropParameterId);
					return free.width > 0 && free.height > 0;
				default:
					return false;
			}
		}
	}

	hasCropParameter(cropParameterId: string) {
		return this.state.indexOf(cropParameterId) > -1;
	}

	getCropParameter<T extends CollectionModelCropParameter = CollectionModelCropParameter>(cropParameterId: string): T {
		let cropParameter = this.cropParameters[cropParameterId] as CollectionModelCropParameter;
		if (cropParameter == null) {
			let cropParameterState = this.collection.state.getFieldValue(
				CollectionModelCropParameterState.cropParameterId(this.captureId, cropParameterId)
			) as ModelStateCropParameter; //FIXME
			if (cropParameterState == null) {
				//if we can't find a state with the current id, we recreate a new state with the same id, and query it again
				this.Mutator.create('free', cropParameterId);
				cropParameterState = this.collection.state.getFieldValue(
					CollectionModelCropParameterState.cropParameterId(this.captureId, cropParameterId)
				) as ModelStateCropParameter;
			}

			switch (cropParameterState.tag) {
				case 'free':
					cropParameter = new CollectionModelFreeCropParameter(this, cropParameterId, this.captureId);
					break;
				default:
					throw new Error(`Capture ${this.captureId}:Unknown brightness correction type: "${cropParameterState.tag}"`);
			}

			this.cropParameters[cropParameterId] = cropParameter;
		}

		return cropParameter as T;
	}

	values(): CollectionModelCropParameter[] {
		const output: CollectionModelCropParameter[] = [];
		for (const cropParameterId of this.state) {
			const cropParameter = this.getCropParameter(cropParameterId);
			output.push(cropParameter);
		}
		return output;
	}

	useInExportCorrections(): boolean {
		let state = false;
		if (this.hasCropParameters()) {
			this.state.forEach((el) => {
				state = this.useInExportCorrection(el);
			});
		}
		return state;
	}

	hasCropParameters(): boolean {
		return this.state.length > 0;
	}

	get capture(): CaptureFileJSONObject | null {
		return this.collection.state.getCapture(this.captureId);
	}

	private get state(): ModelFieldStateCropParameter {
		return (
			(this.collection.state.getFieldValue(
				CollectionModelCropParameterState.captureCropParameterId(this.captureId)
			) as ModelFieldStateCropParameter) || []
		);
	}
}
